import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReferralTable from "@/components/ReferralTable";
import ReferralFormDialog from "@/components/ReferralFormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALL = "__all__";

export default function Referrals() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(ALL);
  const [lane, setLane] = useState(params.get("lane") || ALL);
  const [week, setWeek] = useState(params.get("week") || "");
  const [sev, setSev] = useState(ALL);
  const [dialogOpen, setDialogOpen] = useState(false);

  const canCreate = !!user; // every logged-in user can enter referrals
  const isAdmin = user?.role === "admin";
  const [showCancelled, setShowCancelled] = useState(false);

  const presetsKey = `glh_ref_presets_${user?.email || "anon"}`;
  const [presets, setPresets] = useState([]);
  useEffect(() => {
    try { setPresets(JSON.parse(localStorage.getItem(presetsKey) || "[]")); } catch { setPresets([]); }
  }, [presetsKey]);
  const persistPresets = (list) => { setPresets(list); localStorage.setItem(presetsKey, JSON.stringify(list)); };

  const saveView = () => {
    const name = window.prompt("Name this saved view (e.g. 'My red Hospice'):", "");
    if (!name || !name.trim()) return;
    const next = [...presets.filter((p) => p.name !== name.trim()), { name: name.trim(), f: { q, status, lane, sev, week } }];
    persistPresets(next);
  };
  const applyPreset = (name) => {
    const p = presets.find((x) => x.name === name);
    if (!p) return;
    setQ(p.f.q || ""); setStatus(p.f.status || ALL); setLane(p.f.lane || ALL);
    setSev(p.f.sev || ALL); setWeek(p.f.week || "");
  };
  const deletePreset = (name) => persistPresets(presets.filter((p) => p.name !== name));

  const load = async () => {
    const { data } = await api.get("/referrals", { params: showCancelled ? { include_cancelled: true } : {} });
    setRows(data);
    setLoaded(true);
  };
  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showCancelled]);

  useEffect(() => {
    const l = params.get("lane");
    if (l) setLane(l);
    setWeek(params.get("week") || "");
  }, [params]);

  const laneOptions = useMemo(() => {
    if (!config) return [];
    const counted = config.counted_lanes.map((c) => c.key);
    return counted;
  }, [config]);

  const weekEnd = week ? new Date(new Date(week + "T00:00:00Z").getTime() + 6 * 864e5).toISOString().slice(0, 10) : null;

  const filtered = rows.filter((r) => {
    if (status !== ALL && r.status !== status) return false;
    if (lane !== ALL && r.lane !== lane) return false;
    if (week) {
      const rd = (r.refer_date || "").slice(0, 10);
      if (!(rd >= week && rd <= weekEnd)) return false;
    }
    if (sev !== ALL) {
      if (sev === "stale" && !r.aging.stale) return false;
      if (sev !== "stale" && r.aging.severity !== sev) return false;
    }
    if (q) {
      const s = q.toLowerCase();
      if (!(r.id.toLowerCase().includes(s) || r.initials.toLowerCase().includes(s) || r.lane.toLowerCase().includes(s) || (r.receiving_contact || "").toLowerCase().includes(s) || (r.owner || "").toLowerCase().includes(s))) return false;
    }
    return true;
  });

  const clearLane = () => { setLane(ALL); params.delete("lane"); setParams(params); };
  const clearWeek = () => { setWeek(""); params.delete("week"); setParams(params); };

  return (
    <div className="p-6 space-y-4" data-testid="referrals-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head font-black text-3xl tracking-tight">Referrals</h1>
          <p className="text-sm text-muted-foreground font-mono mt-1">{filtered.length} of {rows.length} · initials + ID only (open a record for PHI)</p>
        </div>
        {canCreate && (
          <Button onClick={() => setDialogOpen(true)} data-testid="new-referral-btn" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold gap-2">
            <Plus className="w-4 h-4" /> New Referral
          </Button>
        )}
      </div>

      <div className="glass rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ID, initials, lane, owner, contact…" data-testid="referral-search" className="pl-8 bg-slate-900/60 border-white/10 h-9" />
        </div>
        <FilterSelect value={status} onChange={setStatus} placeholder="Status" options={config?.all_statuses || []} testid="filter-status" />
        <div className="flex items-center gap-1">
          <FilterSelect value={lane} onChange={setLane} placeholder="Lane" options={laneOptions} testid="filter-lane" />
          {lane !== ALL && <button onClick={clearLane} className="p-1 text-muted-foreground hover:text-foreground" data-testid="clear-lane"><X className="w-4 h-4" /></button>}
        </div>
        <FilterSelect value={sev} onChange={setSev} placeholder="Aging" options={["red", "yellow", "green", "stale"]} testid="filter-severity" />
        {week && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-2.5 py-1.5 text-xs font-mono text-cyan-300" data-testid="week-chip">
            Week of {week.slice(5)}
            <button onClick={clearWeek} data-testid="clear-week" className="hover:text-white"><X className="w-3.5 h-3.5" /></button>
          </span>
        )}
        {isAdmin && (
          <label className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground cursor-pointer select-none ml-auto" data-testid="toggle-cancelled">
            <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} className="accent-cyan-500" />
            Show cancelled
          </label>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="saved-views-bar">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono">Saved views</span>
        {presets.length === 0 && <span className="text-xs text-muted-foreground font-mono">none yet</span>}
        {presets.map((p) => (
          <span key={p.name} className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-slate-900/50 pl-2.5 pr-1 py-1 text-xs font-mono" data-testid={`preset-${p.name}`}>
            <button onClick={() => applyPreset(p.name)} data-testid={`apply-preset-${p.name}`} className="text-cyan-300 hover:text-cyan-200">{p.name}</button>
            <button onClick={() => deletePreset(p.name)} data-testid={`delete-preset-${p.name}`} className="text-muted-foreground hover:text-red-300"><X className="w-3 h-3" /></button>
          </span>
        ))}
        <button onClick={saveView} data-testid="save-view-btn" className="inline-flex items-center gap-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 px-2.5 py-1 text-xs font-mono hover:bg-cyan-500/20 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Save current view
        </button>
      </div>

      {loaded ? (
        <ReferralTable rows={filtered} />
      ) : (
        <div className="glass rounded-xl px-3 py-10 text-center text-muted-foreground font-mono text-xs">Loading referrals…</div>
      )}

      <ReferralFormDialog open={dialogOpen} onOpenChange={setDialogOpen} config={config} onSaved={load} />
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-[150px] bg-slate-900/60 border-white/10 h-9 text-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">All {placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
